import { formatTimeLogDateTimeLabel } from "@jugl-web/domain-resources/task-time-tracking/utils";
import { LogMode } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/LogMode";
import { TaskTimeLogModel } from "@jugl-web/rest-api/tasks/models/TaskTimeLog/TaskTimeLogModel";
import { Menu, PlainButton } from "@jugl-web/ui-components";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { formatSecondsToDuration } from "@jugl-web/utils/date-time/formatSecondsToDuration";
import { differenceInSeconds } from "date-fns";
import { FC } from "react";
import { ManuallyChangedLogIndicator } from "../../../ManuallyChangedLogIndicator";
import { TimerTextWrapper } from "../../../TimerTextWrapper";
import { ReactComponent as ChevronRightIcon } from "../../assets/chevron-right.svg";
import { ReactComponent as MoreIcon } from "../../assets/more.svg";
import { ReactComponent as PenIcon } from "../../assets/pen.svg";
import { ReactComponent as TrashIcon } from "../../assets/trash.svg";
import { TimeTrackingHistoryProps } from "../../types";

interface HistoryListItemProps {
  timeLog: TaskTimeLogModel;
  onEditTimeLog?: TimeTrackingHistoryProps["onEditTimeLog"];
  onDeleteTimeLog?: TimeTrackingHistoryProps["onDeleteTimeLog"];
}

export const HistoryListItem: FC<HistoryListItemProps> = ({
  timeLog,
  onEditTimeLog,
  onDeleteTimeLog,
}) => {
  const { isMobile } = useAppVariant();

  const { t } = useTranslations();

  const isInteractive = isMobile && !!onEditTimeLog;

  const formattedDuration = formatSecondsToDuration(
    differenceInSeconds(timeLog.endDate || new Date(), timeLog.startDate)
  );

  const formattedDateTimeLabel = formatTimeLogDateTimeLabel(timeLog);

  return (
    <div
      className={cx(
        "flex h-10 items-center justify-between gap-2 px-2",
        isInteractive && "jugl__mobile-pressable"
      )}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? () => onEditTimeLog?.(timeLog) : undefined}
    >
      <div className="flex items-center gap-3">
        <TimerTextWrapper>
          <span className="font-secondary text-sm leading-[140%] font-semibold tracking-[0.14px] text-dark">
            {formattedDuration}
          </span>
        </TimerTextWrapper>

        <div className="flex items-center font-secondary text-xs leading-[150%] text-dark-600">
          {formattedDateTimeLabel}
        </div>
      </div>
      <div className="flex items-center gap-1">
        {timeLog.logMode !== LogMode.Auto && <ManuallyChangedLogIndicator />}
        {isMobile ? (
          <ChevronRightIcon className="shrink-0" />
        ) : (
          <Menu
            placement="bottom-end"
            sections={[
              [
                {
                  id: "edit",
                  icon: <PenIcon />,
                  label: t({
                    id: "tasks-page.edit-time-log",
                    defaultMessage: "Edit Log",
                  }),
                  isHidden: !onEditTimeLog,
                  onSelect: (_, close) => {
                    onEditTimeLog?.(timeLog);
                    close();
                  },
                },
                {
                  id: "delete",
                  icon: <TrashIcon />,
                  label: t({
                    id: "tasks-page.delete-time-log",
                    defaultMessage: "Delete Log",
                  }),
                  isHidden: !onDeleteTimeLog,
                  onSelect: (_, close) => {
                    onDeleteTimeLog?.(timeLog);
                    close();
                  },
                },
              ],
            ]}
            renderTrigger={({ Trigger, triggerRef, isOpen }) => (
              <Trigger
                ref={triggerRef}
                as={PlainButton}
                className={cx(
                  "shrink-0 rounded-md transition-colors hover:bg-grey-200",
                  isOpen && "bg-grey-200"
                )}
              >
                <MoreIcon />
              </Trigger>
            )}
          />
        )}
      </div>
    </div>
  );
};
